import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CookieSyncContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simulated interest categories based on content interactions
const INTEREST_CATEGORIES = {
  'anti-aging': ['retinol', 'peptides', 'collagen', 'wrinkles', 'fine lines', 'anti-aging'],
  'acne': ['acne', 'salicylic acid', 'benzoyl peroxide', 'pimples', 'breakouts', 'blemishes'],
  'hydration': ['hyaluronic acid', 'moisturizer', 'dry skin', 'hydration', 'plump'],
  'brightening': ['vitamin c', 'niacinamide', 'dark spots', 'brightening', 'glow', 'radiance'],
  'hair-growth': ['hair growth', 'hair loss', 'scalp', 'follicle', 'thinning', 'regrowth'],
  'hair-care': ['shampoo', 'conditioner', 'hair mask', 'damaged hair', 'frizz', 'shine'],
  'sun-protection': ['sunscreen', 'spf', 'uv', 'sun damage', 'protection'],
  'sensitive-skin': ['sensitive', 'redness', 'irritation', 'gentle', 'calming', 'soothing'],
};

export const useCookieSync = () => {
  const context = useContext(CookieSyncContext);
  if (!context) {
    throw new Error('useCookieSync must be used within CookieSyncProvider');
  }
  return context;
};

export function CookieSyncProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [interests, setInterests] = useState({});
  const [viewHistory, setViewHistory] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [personalizedFeed, setPersonalizedFeed] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedInterests = localStorage.getItem('skinuse_interests');
    const savedViewHistory = localStorage.getItem('skinuse_viewHistory');
    const savedSearchHistory = localStorage.getItem('skinuse_searchHistory');

    if (savedInterests) setInterests(JSON.parse(savedInterests));
    if (savedViewHistory) setViewHistory(JSON.parse(savedViewHistory));
    if (savedSearchHistory) setSearchHistory(JSON.parse(savedSearchHistory));
  }, []);

  // Save data to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('skinuse_interests', JSON.stringify(interests));
  }, [interests]);

  useEffect(() => {
    localStorage.setItem('skinuse_viewHistory', JSON.stringify(viewHistory));
  }, [viewHistory]);

  useEffect(() => {
    localStorage.setItem('skinuse_searchHistory', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // Track content view
  const trackContentView = useCallback((content) => {
    if (!content) return;

    // Add to view history (keep last 50)
    setViewHistory(prev => {
      const newHistory = [
        { 
          content_id: content.content_id, 
          title: content.title, 
          tags: content.tags || [],
          category: content.category,
          timestamp: Date.now() 
        },
        ...prev.filter(h => h.content_id !== content.content_id)
      ].slice(0, 50);
      return newHistory;
    });

    // Update interest scores based on tags and category
    const contentKeywords = [
      ...(content.tags || []),
      content.category || '',
      content.title || ''
    ].join(' ').toLowerCase();

    setInterests(prev => {
      const newInterests = { ...prev };
      
      Object.entries(INTEREST_CATEGORIES).forEach(([category, keywords]) => {
        const matches = keywords.filter(kw => contentKeywords.includes(kw));
        if (matches.length > 0) {
          newInterests[category] = (newInterests[category] || 0) + matches.length * 10;
        }
      });

      return newInterests;
    });

    // Sync to backend if authenticated
    if (isAuthenticated) {
      syncInterestsToBackend();
    }
  }, [isAuthenticated]);

  // Track search query
  const trackSearch = useCallback((query) => {
    if (!query || query.length < 2) return;

    setSearchHistory(prev => {
      const newHistory = [
        { query, timestamp: Date.now() },
        ...prev.filter(h => h.query !== query)
      ].slice(0, 20);
      return newHistory;
    });

    // Update interests based on search
    const queryLower = query.toLowerCase();
    setInterests(prev => {
      const newInterests = { ...prev };
      
      Object.entries(INTEREST_CATEGORIES).forEach(([category, keywords]) => {
        const matches = keywords.filter(kw => queryLower.includes(kw));
        if (matches.length > 0) {
          newInterests[category] = (newInterests[category] || 0) + matches.length * 15;
        }
      });

      return newInterests;
    });
  }, []);

  // Track quiz answer
  const trackQuizAnswer = useCallback((question, answer, isCorrect) => {
    // Extract keywords from question and answer
    const text = `${question} ${answer}`.toLowerCase();
    
    setInterests(prev => {
      const newInterests = { ...prev };
      
      Object.entries(INTEREST_CATEGORIES).forEach(([category, keywords]) => {
        const matches = keywords.filter(kw => text.includes(kw));
        if (matches.length > 0) {
          // Correct answers boost interest more
          const boost = isCorrect ? 20 : 5;
          newInterests[category] = (newInterests[category] || 0) + matches.length * boost;
        }
      });

      return newInterests;
    });
  }, []);

  // Sync interests to backend
  const syncInterestsToBackend = useCallback(async () => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem('skinuse_token');
    if (!token) return;

    try {
      // Get top interests
      const topInterests = Object.entries(interests)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([category]) => category);

      await fetch(`${BACKEND_URL}/api/users/interests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ interests: topInterests })
      });
    } catch (error) {
      console.error('Failed to sync interests:', error);
    }
  }, [interests, isAuthenticated]);

  // Get personalized content feed
  const fetchPersonalizedFeed = useCallback(async () => {
    setLoading(true);
    
    try {
      // Get top interest categories
      const topInterests = Object.entries(interests)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([category]) => category);

      // Build search terms from interests
      const searchTerms = topInterests.flatMap(cat => 
        INTEREST_CATEGORIES[cat]?.slice(0, 2) || []
      );

      // Fetch content matching interests
      let content = [];
      
      if (searchTerms.length > 0) {
        const randomTerm = searchTerms[Math.floor(Math.random() * searchTerms.length)];
        const res = await fetch(`${BACKEND_URL}/api/content/search?q=${encodeURIComponent(randomTerm)}&limit=10`);
        if (res.ok) {
          content = await res.json();
        }
      }

      // If no personalized content, fetch recent content
      if (content.length < 5) {
        const res = await fetch(`${BACKEND_URL}/api/content?limit=10`);
        if (res.ok) {
          const allContent = await res.json();
          // Filter out already included content
          const existingIds = new Set(content.map(c => c.content_id));
          const additionalContent = allContent.filter(c => !existingIds.has(c.content_id));
          content = [...content, ...additionalContent].slice(0, 10);
        }
      }

      // Sort by relevance to interests
      content.sort((a, b) => {
        const scoreA = calculateRelevanceScore(a, interests);
        const scoreB = calculateRelevanceScore(b, interests);
        return scoreB - scoreA;
      });

      setPersonalizedFeed(content);
    } catch (error) {
      console.error('Failed to fetch personalized feed:', error);
    } finally {
      setLoading(false);
    }
  }, [interests]);

  // Calculate relevance score for content
  const calculateRelevanceScore = (content, userInterests) => {
    let score = 0;
    const contentText = [
      ...(content.tags || []),
      content.category || '',
      content.title || ''
    ].join(' ').toLowerCase();

    Object.entries(INTEREST_CATEGORIES).forEach(([category, keywords]) => {
      const interestScore = userInterests[category] || 0;
      const matches = keywords.filter(kw => contentText.includes(kw)).length;
      score += matches * interestScore;
    });

    return score;
  };

  // Get top interests for display
  const getTopInterests = useCallback(() => {
    return Object.entries(interests)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([category, score]) => ({
        category,
        score,
        label: category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
      }));
  }, [interests]);

  // Clear all tracking data
  const clearTrackingData = useCallback(() => {
    setInterests({});
    setViewHistory([]);
    setSearchHistory([]);
    setPersonalizedFeed([]);
    localStorage.removeItem('skinuse_interests');
    localStorage.removeItem('skinuse_viewHistory');
    localStorage.removeItem('skinuse_searchHistory');
  }, []);

  const value = {
    interests,
    viewHistory,
    searchHistory,
    personalizedFeed,
    loading,
    trackContentView,
    trackSearch,
    trackQuizAnswer,
    fetchPersonalizedFeed,
    getTopInterests,
    clearTrackingData,
    syncInterestsToBackend,
  };

  return (
    <CookieSyncContext.Provider value={value}>
      {children}
    </CookieSyncContext.Provider>
  );
}
